import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  CreditCard,
  Database,
  RefreshCw,
  Wallet,
  XCircle,
  Crown,
  Zap,
  Star,
  ArrowLeft,
  CalendarDays,
  Clock,
  BadgeCheck,
} from 'lucide-react';
import { api } from '../api.js';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  .pr { --ink:#0a0a0f;--ink2:#111118;--ink3:#1a1a24;
    --border:rgba(255,255,255,0.07);--text:#f0eff8;--text2:#7e7d99;--text3:#454460;
    --accent:#8a6eff;--accent-s:rgba(138,110,255,0.12);--accent-g:rgba(138,110,255,0.3);
    --ok:#38e09b;--ok-s:rgba(56,224,155,0.1);--er:#ff5c7c;--er-s:rgba(255,92,124,0.1);
    font-family:'Inter',system-ui,-apple-system,sans-serif;background:var(--ink);min-height:100%;color:var(--text);
    flex:1;overflow-y:auto;overflow-x:hidden;
  }
  .pr *{box-sizing:border-box;margin:0;padding:0}
  .pr-bg{position:fixed;inset:0;pointer-events:none;z-index:0;
    background:radial-gradient(ellipse 80% 50% at 60% -10%,rgba(138,110,255,0.11) 0%,transparent 70%),
               radial-gradient(ellipse 50% 40% at 5% 100%,rgba(56,224,155,0.06) 0%,transparent 60%)}
  .pr-wrap{position:relative;z-index:1;max-width:840px;margin:0 auto;padding:48px 24px 80px}

  .pr-back{display:inline-flex;align-items:center;gap:6px;color:var(--text3);font-size:13px;
    cursor:pointer;margin-bottom:24px;border:none;background:none;font-family:'Inter',system-ui,sans-serif;
    transition:color .2s}
  .pr-back:hover{color:var(--text2)}
  .pr-eye{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;letter-spacing:.14em;
    text-transform:uppercase;color:var(--accent);margin-bottom:8px}
  .pr-title{font-family:'Inter',system-ui,sans-serif;font-size:clamp(28px,5vw,40px);font-weight:700;
    line-height:1.1;color:var(--text)}
  .pr-sub{margin-top:10px;font-size:15px;font-weight:300;color:var(--text2);
    line-height:1.6;max-width:440px}

  .toast{display:flex;align-items:center;gap:10px;padding:14px 18px;border-radius:12px;
    font-size:14px;font-weight:500;margin-bottom:24px;animation:fadeUp .3s ease both}
  .toast.ok{background:var(--ok-s);border:1px solid rgba(56,224,155,.2);color:var(--ok)}
  .toast.er{background:var(--er-s);border:1px solid rgba(255,92,124,.2);color:var(--er)}

  .card{background:var(--ink2);border:1px solid var(--border);border-radius:20px;padding:28px;
    margin-bottom:20px;animation:fadeUp .5s ease both;transition:border-color .3s}
  .card:hover{border-color:rgba(255,255,255,.11)}
  .card:nth-child(3){animation-delay:.06s}
  .card:nth-child(4){animation-delay:.12s}
  .clabel{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;letter-spacing:.12em;
    text-transform:uppercase;color:var(--text3);margin-bottom:20px}

  .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:14px}
  .stat{background:var(--ink3);border:1px solid var(--border);border-radius:14px;padding:16px 18px;
    display:flex;flex-direction:column;gap:8px;transition:border-color .25s}
  .stat:hover{border-color:rgba(255,255,255,.12)}
  .stat-ico{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;
    justify-content:center;background:var(--accent-s);color:var(--accent)}
  .stat-lbl{font-size:11px;color:var(--text3);font-weight:500;letter-spacing:.04em;text-transform:uppercase}
  .stat-val{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--text);line-height:1}
  .stat-val.active{color:var(--ok)}
  .stat-meta{font-size:12px;color:var(--text2);line-height:1.35}

  .plans{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:24px}
  .plan{position:relative;background:var(--ink3);border:1.5px solid var(--border);border-radius:16px;
    padding:20px;cursor:pointer;text-align:left;transition:border-color .2s,transform .2s,box-shadow .2s;overflow:hidden}
  .plan::before{content:'';position:absolute;inset:0;
    background:radial-gradient(circle at 50% 0%,var(--accent-s),transparent 70%);
    opacity:0;transition:opacity .25s;pointer-events:none}
  .plan:hover{transform:translateY(-2px)}
  .plan:hover::before{opacity:1}
  .plan.sel{border-color:var(--accent);
    box-shadow:0 0 0 1px var(--accent-g),0 8px 32px rgba(138,110,255,.15)}
  .plan.sel::before{opacity:1}
  .plan-badge{position:absolute;top:12px;right:12px;font-size:10px;font-weight:700;
    letter-spacing:.08em;text-transform:uppercase;padding:3px 8px;border-radius:20px;
    background:var(--accent-s);color:var(--accent);border:1px solid rgba(138,110,255,.3)}
  .plan-badge.sale{right:auto;left:12px;background:rgba(56,224,155,.12);color:var(--ok);border-color:rgba(56,224,155,.25)}
  .plan-badge.muted{background:rgba(255,255,255,.06);color:var(--text2);border-color:var(--border)}
  .plan-ico{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;
    justify-content:center;margin-bottom:12px}
  .plan-name{font-family:'Inter',system-ui,sans-serif;font-size:16px;font-weight:700;color:var(--text);margin-bottom:4px}
  .plan-code{font-size:11px;color:var(--text3);text-transform:uppercase;letter-spacing:.08em;margin-bottom:14px}
  .plan-price{font-family:'Inter',system-ui,sans-serif;font-size:22px;font-weight:700;color:var(--text)}
  .plan-price span{font-size:13px;font-weight:400;color:var(--text2);margin-left:2px}
  .plan-price.free{color:var(--ok)}
  .plan-price-old{display:block;font-size:12px;line-height:1.1;color:var(--text3);text-decoration:line-through;margin-bottom:2px}
  .plan-price-meta{margin-top:8px;font-size:12px;color:var(--text2);line-height:1.4}

  .cycle-switch{display:flex;align-items:center;gap:10px;margin-bottom:18px;flex-wrap:wrap}
  .cycle-pill{display:inline-flex;align-items:center;padding:4px;background:var(--ink3);border:1px solid var(--border);border-radius:999px}
  .cycle-btn{border:none;background:transparent;color:var(--text2);padding:9px 16px;border-radius:999px;font-size:13px;font-weight:700;cursor:pointer;transition:all .2s}
  .cycle-btn.active{background:var(--accent);color:#fff;box-shadow:0 4px 18px rgba(138,110,255,.25)}
  .cycle-btn:disabled{opacity:.4;cursor:not-allowed}
  .cycle-note{font-size:12px;color:var(--text3)}

  .act-row{display:flex;align-items:center;justify-content:space-between;gap:12px;
    flex-wrap:wrap;padding-top:20px;border-top:1px solid var(--border)}
  .act-sum{display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text2)}
  .act-sum strong{color:var(--text);font-weight:600}

  .btn{display:inline-flex;align-items:center;gap:8px;padding:13px 24px;border-radius:12px;
    font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:700;letter-spacing:.02em;
    background:var(--accent);color:#fff;border:none;cursor:pointer;transition:all .2s;
    white-space:nowrap;box-shadow:0 4px 20px var(--accent-g)}
  .btn:hover:not(:disabled){background:#9d86ff;transform:translateY(-1px);
    box-shadow:0 6px 28px rgba(138,110,255,.5)}
  .btn:disabled{opacity:.4;cursor:not-allowed;box-shadow:none}

  .stripe-note{margin-top:14px;font-size:12px;color:var(--text3);display:flex;align-items:center;gap:6px}

  .tbl-wrap{margin-top:8px;overflow:auto;border:1px solid var(--border);border-radius:14px;background:var(--ink3)}
  .tbl{width:100%;border-collapse:collapse;min-width:720px}
  .tbl th,.tbl td{padding:12px 14px;text-align:left;border-bottom:1px solid var(--border);font-size:13px}
  .tbl th{font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:var(--text3);font-weight:700}
  .tbl td{color:var(--text2)}
  .tbl td strong{color:var(--text);font-weight:600}
  .pill{display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
  .pill.ok{background:var(--ok-s);color:var(--ok);border:1px solid rgba(56,224,155,.25)}
  .tbl-empty{padding:16px 14px;color:var(--text3);font-size:13px}

  @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  .spin{animation:spin .8s linear infinite}
`;

const PLAN_ICONS = [
  { icon: <Zap size={18} />, bg: 'rgba(56,224,155,.15)', color: '#38e09b' },
  { icon: <Star size={18} />, bg: 'rgba(138,110,255,.15)', color: '#8a6eff' },
  { icon: <Crown size={18} />, bg: 'rgba(245,200,66,.15)', color: '#f5c842' },
];

const BYTES_PER_GB = 1024 * 1024 * 1024;

function formatMoney(amount, currency = 'MXN') {
  const n = Number(amount || 0);
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: String(currency || 'MXN').toUpperCase(),
    minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let scaled = value;
  let unitIndex = 0;

  while (scaled >= 1024 && unitIndex < units.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }

  const digits = scaled >= 10 || unitIndex === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${units[unitIndex]}`;
}

function getPlanCyclePricing(plan, billingCycle) {
  if (!plan?.pricing) return null;
  return billingCycle === 'annual' ? (plan.pricing.annual || null) : (plan.pricing.monthly || null);
}

function getPlanCycleLabel(billingCycle) {
  return billingCycle === 'annual' ? '/año' : '/mes';
}

export default function ProfilePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const loadData = async () => {
    setLoading(true);
    setActionError('');
    try {
      const [myBilling, publicPlans, myPurchases] = await Promise.all([
        api.getMyBilling(),
        api.getPublicPlans(),
        api.getMyBillingPurchases(),
      ]);
      setBilling(myBilling || null);
      const items = Array.isArray(publicPlans?.items) ? publicPlans.items : [];
      setPlans(items);
      setPurchases(Array.isArray(myPurchases?.items) ? myPurchases.items : []);
      setBillingCycle(myBilling?.subscription?.billingCycle === 'annual' ? 'annual' : 'monthly');

      const currentPlanId = myBilling?.plan?.id || '';
      setSelectedPlanId((prev) => {
        if (prev && items.some((plan) => plan.id === prev)) return prev;
        if (currentPlanId && items.some((plan) => plan.id === currentPlanId)) return currentPlanId;
        if (items.length) return items[0].id;
        return '';
      });
    } catch (err) {
      setActionError(err.message || 'No se pudo cargar la información de suscripción.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const status = query.get('billing');
    if (status === 'success') {
      setActionMsg('Pago completado. Tu suscripción fue actualizada.');
      setActionError('');
      loadData();
    } else if (status === 'cancel') {
      setActionMsg('El pago fue cancelado.');
    }
  }, [query]);

  const currentPlanId = billing?.plan?.id || '';
  const currentPlanCode = String(billing?.plan?.code || '').toLowerCase();
  const currentBillingCycle = billing?.subscription?.billingCycle || 'monthly';
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const selectedPricing = getPlanCyclePricing(selectedPlan, billingCycle);
  const selectedPrice = Number(selectedPricing?.amount || 0);
  const selectedOriginalPrice = Number(selectedPricing?.originalAmount || 0);
  const selectedCurrency = selectedPlan?.currency || billing?.plan?.currency || 'USD';
  const canShowAnnual = plans.some((plan) => Boolean(plan?.pricing?.annual));
  const isActive = String(billing?.status || '').toLowerCase() === 'active';
  const isRenewal = Boolean(currentPlanId && selectedPlanId && selectedPlanId === currentPlanId && selectedPrice > 0);
  const isPlanChange = Boolean(currentPlanId && selectedPlanId && selectedPlanId !== currentPlanId);
  const isCycleChange = Boolean(isRenewal && currentBillingCycle !== billingCycle);
  const storageLimitGb = Number(billing?.limits?.storageGB);
  const storageLimitDefined = billing?.limits?.storageGB != null && storageLimitGb !== -1;
  const rawStorageBytes = Number(billing?.usageMonthToDate?.storageBytes);
  const fallbackStorageBytes = Number(billing?.usageMonthToDate?.storageGB || 0) * BYTES_PER_GB;
  const storageUsedBytes = Number.isFinite(rawStorageBytes) ? rawStorageBytes : fallbackStorageBytes;
  const storageLimitBytes = storageLimitDefined ? Math.max(0, storageLimitGb * BYTES_PER_GB) : -1;
  const storageRemainingBytes = storageLimitDefined ? Math.max(0, storageLimitBytes - storageUsedBytes) : -1;

  useEffect(() => {
    if (billingCycle === 'annual' && selectedPlan && !selectedPlan?.pricing?.annual) {
      setBillingCycle('monthly');
    }
  }, [billingCycle, selectedPlan]);

  const handleBuyOrRenew = async () => {
    if (!selectedPlanId) return;
    setSubmitting(true);
    setActionError('');
    setActionMsg('');

    try {
      const requestPayload = {
        planId: selectedPlanId,
        billingCycle,
        successUrl: `${window.location.origin}/profile?billing=success`,
        cancelUrl: `${window.location.origin}/profile?billing=cancel`,
      };

      console.log('[billing][checkout] request', requestPayload);

      const result = await api.createStripeCheckoutSession({
        planId: selectedPlanId,
        billingCycle,
        successUrl: `${window.location.origin}/profile?billing=success`,
        cancelUrl: `${window.location.origin}/profile?billing=cancel`,
      });

      console.log('[billing][checkout] response', result);

      if (result?.skipCheckout) {
        setActionMsg('Plan actualizado correctamente.');
        await loadData();
      } else if (result?.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      } else {
        setActionError('No se pudo iniciar el checkout.');
      }
    } catch (err) {
      setActionError(err.message || 'No fue posible procesar la suscripción.');
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = (() => {
    if (!selectedPlanId) return 'Selecciona un plan';
    if (!currentPlanId) return selectedPrice > 0 ? 'Comprar plan' : 'Activar plan';
    if (selectedPlanId === currentPlanId) {
      if (selectedPrice <= 0) return 'Mantener plan gratis';
      if (isCycleChange) return billingCycle === 'annual' ? 'Cambiar a cobro anual' : 'Cambiar a cobro mensual';
      return billingCycle === 'annual' ? 'Renovar anualidad' : 'Renovar plan actual';
    }
    return selectedPrice > 0 ? 'Cambiar y pagar plan' : 'Cambiar a plan gratis';
  })();

  if (loading) {
    return (
      <div className="pr">
        <style>{styles}</style>
        <div className="pr-bg" />
        <div className="pr-wrap">
          <div className="card">
            <div className="clabel">Mi cuenta</div>
            <p>Cargando perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pr">
      <style>{styles}</style>
      <div className="pr-bg" />
      <div className="pr-wrap">
        <div style={{ marginBottom: 40, animation: 'fadeUp .45s ease both' }}>
          <button className="pr-back" type="button" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={14} /> Volver al dashboard
          </button>
          <div className="pr-eye">Mi cuenta</div>
          <h1 className="pr-title">Suscripción &amp; plan</h1>
          <p className="pr-sub">Gestiona tu plan activo, renueva tu suscripción o cambia a otro nivel cuando lo necesites.</p>
        </div>

        {(actionMsg || actionError) && (
          <div className={`toast ${actionError ? 'er' : 'ok'}`}>
            {actionError ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
            {actionError || actionMsg}
          </div>
        )}

        <div className="card">
          <div className="clabel">Suscripción actual</div>
          <div className="stats">
            <div className="stat">
              <div className="stat-ico"><BadgeCheck size={16} /></div>
              <div className="stat-lbl">Plan</div>
              <div className="stat-val">{billing?.plan?.name || '—'}</div>
            </div>
            <div className="stat">
              <div className="stat-ico" style={{ background: isActive ? 'rgba(56,224,155,.12)' : 'var(--accent-s)', color: isActive ? 'var(--ok)' : 'var(--accent)' }}>
                <Zap size={16} />
              </div>
              <div className="stat-lbl">Estado</div>
              <div className={`stat-val ${isActive ? 'active' : ''}`}>{billing?.status || '—'}</div>
            </div>
            <div className="stat">
              <div className="stat-ico"><CalendarDays size={16} /></div>
              <div className="stat-lbl">Periodo actual</div>
              <div className="stat-val" style={{ fontSize: 13 }}>{formatDate(billing?.subscription?.currentPeriodStart)}</div>
            </div>
            <div className="stat">
              <div className="stat-ico"><Clock size={16} /></div>
              <div className="stat-lbl">Próxima renovación</div>
              <div className="stat-val" style={{ fontSize: 13 }}>{formatDate(billing?.subscription?.currentPeriodEnd)}</div>
            </div>
            <div className="stat">
              <div className="stat-ico"><Database size={16} /></div>
              <div className="stat-lbl">Almacenamiento usado</div>
              <div className="stat-val" style={{ fontSize: 14 }}>{formatBytes(storageUsedBytes)}</div>
              <div className="stat-meta">
                {storageLimitDefined
                  ? `${formatBytes(storageRemainingBytes)} disponibles de ${storageLimitGb} GB`
                  : 'Almacenamiento ilimitado en tu plan'}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="clabel">Comprar, renovar o cambiar plan</div>
          <div className="cycle-switch">
            <div className="cycle-pill">
              <button
                type="button"
                className={`cycle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Mensual
              </button>
              <button
                type="button"
                className={`cycle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                onClick={() => setBillingCycle('annual')}
                disabled={!canShowAnnual}
              >
                Anual
              </button>
            </div>
            <div className="cycle-note">
              {canShowAnnual
                ? 'Algunos planes permiten facturación anual con descuento.'
                : 'Por ahora tus planes disponibles sólo están configurados para cobro mensual.'}
            </div>
          </div>
          <div className="plans">
            {plans.map((plan, i) => {
              const ico = PLAN_ICONS[i % PLAN_ICONS.length];
              const pricing = getPlanCyclePricing(plan, billingCycle) || getPlanCyclePricing(plan, 'monthly');
              const isShowingFallbackMonthly = billingCycle === 'annual' && !plan?.pricing?.annual;
              const price = Number(pricing?.amount || 0);
              const originalPrice = Number(pricing?.originalAmount || 0);
              const hasDiscount = originalPrice > price;
              return (
                <button
                  key={plan.id}
                  type="button"
                  className={`plan ${selectedPlanId === plan.id ? 'sel' : ''}`}
                  onClick={() => setSelectedPlanId(plan.id)}
                >
                  {currentPlanId === plan.id && <span className="plan-badge">Actual</span>}
                  {hasDiscount && price > 0 && <span className="plan-badge sale">Ahorra {Math.round(Number(pricing?.totalDiscountPercent || 0))}%</span>}
                  {isShowingFallbackMonthly && <span className="plan-badge muted">Solo mensual</span>}
                  <div className="plan-ico" style={{ background: ico.bg, color: ico.color }}>{ico.icon}</div>
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-code">{String(plan.code || '').toUpperCase()}</div>
                  <div className={`plan-price ${price === 0 ? 'free' : ''}`}>
                    {price > 0 ? (
                      <>
                        {hasDiscount && <span className="plan-price-old">{formatMoney(originalPrice, plan.currency)}</span>}
                        {formatMoney(price, plan.currency)}<span>{getPlanCycleLabel(isShowingFallbackMonthly ? 'monthly' : billingCycle)}</span>
                      </>
                    ) : 'Gratis'}
                  </div>
                  {price > 0 && pricing && (
                    <div className="plan-price-meta">
                      {billingCycle === 'annual' && !isShowingFallbackMonthly
                        ? `${formatMoney(pricing.equivalentMonthlyAmount, plan.currency)}/mes equivalente`
                        : hasDiscount
                          ? `Precio promocional activo`
                          : 'Sin descuento adicional'}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="act-row">
            <div className="act-sum">
              <Wallet size={15} />
              {selectedPlan
                ? <><strong>{selectedPlan.name}</strong>&nbsp;·&nbsp;{selectedPrice > 0 ? `${formatMoney(selectedPrice, selectedCurrency)}${getPlanCycleLabel(billingCycle)}` : 'Gratis'}</>
                : 'Selecciona un plan'}
            </div>
            <button
              className="btn"
              type="button"
              onClick={handleBuyOrRenew}
              disabled={!selectedPlanId || submitting || (selectedPlanId === currentPlanId && currentPlanCode === 'free')}
            >
              {submitting
                ? <><RefreshCw size={14} className="spin" /> Procesando...</>
                : <><CreditCard size={14} /> {actionLabel}</>}
            </button>
          </div>

          <div className="stripe-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" opacity=".5">
              <path d="M12 1C5.93 1 1 5.93 1 12s4.93 11 11 11 11-4.93 11-11S18.07 1 12 1zm.5 16.5h-1v-6h1v6zm0-8h-1V7.5h1V9.5z" />
            </svg>
            Pagos procesados de forma segura en Stripe · {selectedCurrency} · Los planes gratuitos se activan sin checkout
          </div>

          <div className="stripe-note" style={{ marginTop: 8 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" opacity=".5">
              <path d="M12 1C5.93 1 1 5.93 1 12s4.93 11 11 11 11-4.93 11-11S18.07 1 12 1zm.5 16.5h-1v-6h1v6zm0-8h-1V7.5h1V9.5z" />
            </svg>
            {isRenewal
              ? isCycleChange
                ? `Cambio de ciclo: tu renovación se extiende usando el ciclo ${billingCycle === 'annual' ? 'anual' : 'mensual'} seleccionado.`
                : `Renovación anticipada: se suma ${billingCycle === 'annual' ? '1 año' : '1 mes'} al final de tu vigencia actual.`
              : isPlanChange
                ? `Cambio de plan: la vigencia se reinicia desde hoy y termina en ${billingCycle === 'annual' ? '1 año' : '30 días'}.`
                : `La renovación suma al final de tu vigencia actual; cambiar de plan reinicia la vigencia a ${billingCycle === 'annual' ? '1 año' : '30 días'} desde la compra.`}
          </div>
        </div>

        <div className="card">
          <div className="clabel">Últimas 10 compras</div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Plan</th>
                  <th>Evento</th>
                  <th>Monto</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {purchases.length > 0 ? purchases.map((purchase) => (
                  <tr key={`${purchase.id}-${purchase.createdAt}`}>
                    <td>{formatDate(purchase.createdAt)}</td>
                    <td><strong>{purchase.planName || 'Plan'}</strong></td>
                    <td>{purchase.eventType === 'invoice.paid' ? 'Renovación' : 'Checkout completado'}</td>
                    <td><strong>{formatMoney(purchase.amount, purchase.currency || 'MXN')}</strong></td>
                    <td><span className="pill ok">Pagado</span></td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5}>
                      <div className="tbl-empty">Aún no hay compras registradas.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
